import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { HIDE_NOTIFICATION, ROLES, USER_DATA } from '~/constant';
import type { User } from '~/types/user';

export interface AuthState {
	user: User | null;
	token: string | null;
	avatar: string | null;
	setAvatar: (avatar: string) => void;
	companyCode: string | null;
	setCompanyCode: (companyCode: string) => void;
	isStaff: boolean;
	isAuthenticated: boolean;
	idForm: number;
	role: string;
	isLoadingAuth: boolean; // Quản lý trạng thái loading khi kiểm tra auth
	loginAction: (userData: User, accessToken: string, newRefreshToken?: string) => void;
	logoutAction: (navigate: any, redirect?: boolean) => void; // navigate để điều hướng sau khi logout
	checkAuthStatusAction: (pathname: string, navigate: any) => Promise<void>; // navigate để điều hướng
	setLoadingAuthAction: (loading: boolean) => void;
	updateUserAction: (data: Partial<User>) => void;
	_hasHydrated: boolean;
	setHasHydrated: (state: boolean) => void;
	setIdForm: (idForm: number) => void;
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			user: null,
			avatar: null,
			idForm: 0,
			role: ROLES.AGENT,
			token: null,
			isAuthenticated: false,
			isLoadingAuth: true,
			isStaff: false,
			companyCode: null,
			_hasHydrated: false,
			setHasHydrated: (state) => set({ _hasHydrated: state }),
			setLoadingAuthAction: (loading) => set({ isLoadingAuth: loading }),
			setAvatar: (avatar) => set({ avatar }),
			setCompanyCode: (companyCode) => set({ companyCode }),
			setIdForm: (idForm) => set({ idForm }),
			updateUserAction: (data) => {
				const currentUser = get().user;
				if (currentUser) {
					set({ user: { ...currentUser, ...data } });
				}
			},
			// ... (các action khác giữ nguyên)
			loginAction: (userData, accessToken) => {
				set({
					user: userData,
					token: accessToken,
					isAuthenticated: true,
					isLoadingAuth: false,
					role: userData.role || ROLES.AGENT,
					avatar: userData?.agent_avatar || userData?.staff_avatar,
					companyCode: userData?.code_key,
				});
			},
			logoutAction: (navigate, redirect = true) => {
				set({
					user: null,
					role: ROLES.AGENT,
					token: null,
					isAuthenticated: false,
					isLoadingAuth: false,
				});
				if (typeof window !== 'undefined' && redirect) {
					localStorage.removeItem(HIDE_NOTIFICATION);
					if (redirect) {
						if (navigate) navigate({ to: '/login' });
						window.location.href = '/login';
					}
				}
			},
			checkAuthStatusAction: async (pathname, navigate) => {
				const token = get().token;
				const user = get().user;

				if (pathname === '/register') {
					if (token && user) {
						get().logoutAction(navigate, false);
					}
					set({ isLoadingAuth: false });
					return;
				}

				// Handle /samtek/login path
				if (pathname === '/samtek/login') {
					if (token && user?.role) {
						// User is logged in, check if role is samtek
						if (user.role !== ROLES.SAMTEK) {
							// Wrong role, logout without redirect
							get().logoutAction(navigate, false);
							set({ isLoadingAuth: false });
							return;
						}
						// Correct role (samtek), redirect to samtek dashboard
						set({
							isAuthenticated: true,
							isLoadingAuth: false,
							user,
							token,
							role: user.role,
						});
						navigate({
							to: user?.role === ROLES.SAMTEK ? '/samtek/dashboard' : '/dashboard',
						});
						return;
					}
					// No user logged in, don't redirect anywhere
					set({
						isAuthenticated: false,
						isLoadingAuth: false,
						user: null,
						token: null,
						role: ROLES.AGENT,
					});
					return;
				}

				if (pathname === '/login') {
					const token = get().token;
					const user = get().user;

					if (token && user?.role) {
						set({
							isAuthenticated: true,
							isLoadingAuth: false,
							user,
							token,
							role: user.role || '',
						});
						navigate({
							to: user?.role === ROLES.SAMTEK ? '/samtek/customers' : `/${user.role}/dashboard`,
						});
						return;
					}
					set({
						isAuthenticated: false,
						isLoadingAuth: false,
						user: null,
						token: null,
						role: ROLES.AGENT,
					});
					return;
				}
				// Check if samtek user tries to access staff or agent paths
				if (token && user?.role === ROLES.SAMTEK) {
					if (pathname.startsWith('/staff') || pathname.startsWith('/agent')) {
						// Samtek user trying to access staff/agent paths, logout and redirect to /login
						get().logoutAction(navigate, true);
						return;
					}
				}

				if (token && user?.role && pathname === '/') {
					navigate({ to: `/${user.role}/dashboard` });
				}
				set({ isLoadingAuth: true });
				const currentToken = get().token;
				if (!currentToken) {
					get().logoutAction(navigate); // Gọi logout của store để xử lý state và redirect
					return;
				}
			},
		}),
		{
			name: USER_DATA,
			onRehydrateStorage: () => (state) => {
				state?.setHasHydrated(true);
			},
		},
	),
);
